import { useState } from "react";
import { cn } from "@/lib/utils";

interface Step3TopicsProps {
  onNext: (selectedStages: string[]) => void;
  onSkip: () => void;
  onBack: () => void;
  selectedTopics?: string[];
  onChange?: (stages: string[]) => void;
}

const STAGES = [
  { id: "awareness", label: "Awareness", description: "Problem-focused prompts (e.g., \"How to fix...\")." },
  { id: "evaluation", label: "Evaluation", description: "Solution comparison prompts (e.g., \"Best tools for...\")." },
  { id: "decision", label: "Decision", description: "Brand-specific prompts (e.g., \"Pricing for...\")." },
  { id: "retention", label: "Retention", description: "Support & how-to prompts." },
  { id: "advocacy", label: "Advocacy", description: "Community & reviews." },
];

export function StageSelection({ onNext, onSkip, onBack, selectedTopics: externalSelectedTopics, onChange }: Step3TopicsProps) {
  const [internalSelectedStages, setInternalSelectedStages] = useState<string[]>([]);
  const selectedStages = externalSelectedTopics || internalSelectedStages;

  const toggleStage = (id: string) => {
    const newStages = selectedStages.includes(id)
      ? selectedStages.filter((s) => s !== id)
      : [...selectedStages, id];
    
    if (onChange) {
      onChange(newStages);
    } else {
      setInternalSelectedStages(newStages);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Back button */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-950 transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      )}
      
      <div className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">
          Select Buyer Journey Stages
        </h1>
        <p className="text-gray-500 text-sm hidden lg:block">
          Choose which stages of the buyer journey to track.
        </p>
      </div>

      <div className="space-y-3">
        {STAGES.map((stage) => {
          const isSelected = selectedStages.includes(stage.id);
          return (
            <button
              key={stage.id}
              onClick={() => toggleStage(stage.id)}
              className={cn(
                "w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-all",
                isSelected
                  ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600"
                  : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
              )}
            >
              <div
                className={cn(
                  "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                  isSelected
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-slate-300 bg-white"
                )}
              >
                {isSelected && (
                  <svg
                    className="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
              <div className="space-y-1">
                <span className={cn("block text-sm font-medium", isSelected ? "text-blue-900" : "text-slate-900")}>
                  {stage.label}
                </span>
                <span className={cn("hidden lg:block text-xs", isSelected ? "text-blue-700" : "text-slate-500")}>
                  {stage.description}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="space-y-3 pt-2">
        <button
          onClick={() => onNext(selectedStages)}
          className="w-full rounded-md inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-blue-600 text-white hover:bg-blue-700 h-10 px-4 py-2 text-sm shadow-sm"
        >
          Continue
        </button>
        <button
          onClick={() => {
            // Clear stages when skipping
            if (onChange) {
              onChange([]);
            } else {
              setInternalSelectedStages([]);
            }
            onSkip();
          }}
          className="w-full rounded-md inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-slate-500 hover:text-slate-900 h-9 px-3 text-sm"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
